'use client';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { useTranslations } from 'next-intl';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import VideoLazyLoad from '@/app/components/shared/VideoLazyLoad';
import { cn } from '@/app/utils';
import { getVideoResources } from './utils';

// 将视频项组件提取为独立的记忆化组件
const VideoItem = memo(
  ({
    url,
    poster,
    mobilePoster,
    intlKey,
    title,
    isSelected,
    shouldPreload,
  }: {
    url: string;
    poster: string;
    mobilePoster?: string;
    intlKey: string;
    title: string;
    isSelected: boolean;
    shouldPreload: boolean;
  }) => {
    const enabled = isSelected || shouldPreload;

    return (
      <div
        key={`scene-${intlKey}`}
        className={cn(
          'relative mx-1 flex h-full min-w-0 shrink-0 grow-0 basis-[931px] flex-col items-center justify-center md:basis-full',
          !isSelected && 'opacity-20',
        )}
      >
        {/* 原始视频：仅选中项加载并播放 */}
        <div className={cn('relative w-full', !isSelected && 'blur-sm')}>
          <VideoLazyLoad
            poster={poster}
            mobilePoster={mobilePoster}
            videoSrc={url}
            className="h-[520px] w-full"
            videoClassName="object-center"
            enabled={enabled}
            shouldPlay={isSelected}
            preload={isSelected ? 'metadata' : 'none'}
            alt={title}
          />
        </div>

        {/* 视频倒影：仅选中项加载并播放 */}
        <div className={cn('relative h-[200px] w-full overflow-hidden lg:h-[60px]', !isSelected && 'blur-sm')}>
          <VideoLazyLoad
            poster={poster}
            mobilePoster={mobilePoster}
            videoSrc={url}
            className="h-[520px] w-full scale-y-[-1] object-cover opacity-40"
            videoClassName="object-center"
            enabled={enabled}
            shouldPlay={isSelected}
            preload={isSelected ? 'metadata' : 'none'}
            alt={`${title} reflection`}
          />
          <div className="absolute inset-0 z-10 bg-gradient-to-b from-transparent to-black opacity-60" />
        </div>
      </div>
    );
  },
);

VideoItem.displayName = 'VideoItem';

export const Scene = () => {
  const t = useTranslations('home3.scene');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoResources = useMemo(() => getVideoResources(t), [t]);

  // 内容轮播实例
  const autoplayRef = useRef<ReturnType<typeof Autoplay>>(Autoplay({ delay: 3000, stopOnInteraction: false }));
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'center',
      containScroll: 'trimSnaps',
    },
    [autoplayRef.current],
  );

  // 标题轮播实例（PC 端用）
  const [titleEmblaRef, titleEmblaApi] = useEmblaCarousel({
    loop: false,
    align: 'center',
    dragFree: true,
    containScroll: false,
  });

  useEffect(() => {
    if (!sectionRef.current || !emblaApi) return;

    // autoplay 可见性控制
    const observer = new IntersectionObserver(
      (entries) => {
        const autoplay = emblaApi.plugins().autoplay;
        if (!autoplay) return;
        if (entries[0].isIntersecting) {
          autoplay.play();
        } else {
          autoplay.stop();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sectionRef.current);

    // 轮播选中项同步
    const onInit = () => setScrollSnaps(emblaApi.scrollSnapList() || []);
    const onSelect = () => {
      const index = emblaApi.selectedScrollSnap() || 0;
      setSelectedIndex(index);
      // 同步 PC 端标题轮播位置（移动端标题轮播不存在，titleEmblaApi 为 null 时直接跳过）
      titleEmblaApi?.scrollTo(index);
    };

    onInit();
    onSelect();
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);

    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
      emblaApi.plugins().autoplay?.stop();
      emblaApi.off('reInit', onInit).off('reInit', onSelect).off('select', onSelect);
    };
  }, [emblaApi, titleEmblaApi]);

  const handleTitleClick = (index: number) => {
    emblaApi?.scrollTo(index);
    autoplayRef.current?.reset();
  };

  // 记忆化视频项列表渲染
  const renderVideoItems = useMemo(() => {
    return (
      <div className="relative flex">
        {videoResources.map(({ url, poster, mobilePoster, intlKey, title }, index) => (
          <VideoItem
            key={`scene-${intlKey}`}
            url={url}
            poster={poster}
            mobilePoster={mobilePoster}
            intlKey={intlKey}
            title={title}
            isSelected={selectedIndex === index}
            shouldPreload={false}
          />
        ))}
      </div>
    );
  }, [videoResources, selectedIndex]);

  return (
    <div className="relative" ref={sectionRef}>
      <div className="flex items-center justify-center">
        <div className="mt-[160px] flex w-full flex-col items-center lg:mt-[30px]">
          <h2 className="text-[42px] md:mx-[50px] md:text-center md:text-[36px] md:leading-none">{t('title')}</h2>

          {/* 移动端标题：当前选中项名称（md 及以下可见） */}
          <div className="hidden w-full text-center md:block">
            <div className="mt-2 font-semibold text-[36px] text-system-blue04 md:mt-0 md:leading-[50px]">
              {videoResources[selectedIndex].title}
            </div>
          </div>

          {/* PC 端标题轮播（md 以上可见） */}
          <div className="flex w-full flex-col items-center justify-center md:hidden">
            <div className="relative w-full overflow-hidden">
              <div className="pointer-events-none absolute top-0 left-0 z-10 h-full w-[15%] bg-gradient-to-r from-black to-transparent" />
              <div ref={titleEmblaRef} className="overflow-hidden">
                <div className="flex justify-center">
                  {scrollSnaps.map((_, index) => (
                    <div
                      key={`title-item-${videoResources[index].intlKey}`}
                      className={cn(
                        'shrink-0 cursor-pointer whitespace-nowrap px-8 text-[42px] text-white opacity-30 transition-all duration-300 hover:text-system-white hover:opacity-100 md:font-semibold md:text-[36px] lg:text-[32px]',
                        selectedIndex === index && 'text-system-blue01 opacity-100',
                      )}
                      onClick={() => handleTitleClick(index)}
                    >
                      <span className="inline-block font-semibold">{videoResources[index].title}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="pointer-events-none absolute top-0 right-0 z-10 h-full w-[15%] bg-gradient-to-l from-black to-transparent" />
            </div>
          </div>

          <div className="relative mt-12 flex w-full items-center gap-16 md:mt-0">
            <div className="relative mx-auto w-full">
              <div ref={emblaRef} className="overflow-hidden">
                {renderVideoItems}
              </div>
            </div>
            <div className="absolute right-0 bottom-0 left-0 flex h-[200px] w-full items-center justify-center bg-gradient-to-b from-transparent to-black backdrop-blur-[10px] lg:h-[80px]" />
          </div>
        </div>
      </div>
    </div>
  );
};
